import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import InlineCheckoutForm from '../../components/InlineCheckoutForm/InlineCheckoutForm';
import TrustStrip from '../../components/TrustStrip';
import { buildSalesLeadCheckoutParams } from '../../utils/salesLeadCheckoutParams';
import useSalesPromptRouting from '../../hooks/useSalesPromptRouting';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';

// Import base styles and then our custom overrides
import './SalesPage.css';
import './SalesPage08.css';
import '../../components/InlineCheckoutForm/InlineCheckoutForm.css';

export default function SalesPage08() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showLoginHint,
    loginReturnTo,
    getLeadSuccessPath,
  } = useSalesPromptRouting({ pageVariant: 'v08_paradigm_shift' });

  // Bonus offer state - checkbox defaults to checked, timer starts at 5 minutes
  const [bonusOptIn, setBonusOptIn] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes = 300 seconds

  // Countdown timer - resets on each page load (no persistence)
  useEffect(() => {
    if (timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timeRemaining]);

  // Format seconds as M:SS
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  
  useEffect(() => {
    window.__experimentContext = {
      experiment_name: 'sales_page_design_08_test',
      experiment_variant: 'v08_paradigm_shift',
    };
    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page: 'sales_v08_paradigm_shift',
      variant: 'v08_paradigm_shift',
      ...getUtmParamsForApi()
    });
  }, [location]);

  const handleLoginClick = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: 'sales_v08_link',
      source: location.pathname
    });
    navigate(`/login?returnTo=${encodeURIComponent(loginReturnTo)}`, {
      state: buildAuthOverlayRouteState(location),
    });
  };

  const handleContactSuccess = ({ contactData, leadTransactionId, autoLoginToken, howFoundUs, howFoundUsOtherText, existingUser, redirectTo }) => {
    if (existingUser && redirectTo) {
      navigate(redirectTo);
      return;
    }

    const params = buildSalesLeadCheckoutParams({
      contactData,
      leadTransactionId,
      variant: 'v08_paradigm_shift',
      howFoundUs,
      howFoundUsOtherText,
      extra: { bonusOptIn: bonusOptIn.toString() },
    });
    navigate(getLeadSuccessPath(params, autoLoginToken));
  };

  return (
    <>
      <Helmet>
        <title>BotSpot | A Permanent Shift in How You Invest</title>
      </Helmet>

      {/* 
          Inheriting .sales-page-minimal for structure 
          Applying .v08-paradigm-theme for visual skin 
      */}
      <div className="sales-page-minimal v08-paradigm-theme">
        
        {/* Background Layer Inherited from SalesPage.css */}
        <div className="hero-background">
          <div className="v08-horizon-glow"></div>
          <div className="v08-slow-orb"></div>
        </div>

        <Container>
          {/* Using base .content-wrapper and .hero-content for perfect alignment */}
          <div className="content-wrapper">
            <div className="hero-content">
              
              {/*<div className="v08-minimal-badge">
                Intelligent Trading Systems
              </div>*/}

              <TrustStrip className="pricing-trust-strip v08-trust-strip-spacing" />

              <h1 className="hero-title v08-headline">
                Change how you invest<br />
                <span className="v08-gradient-text">FOREVER</span>
              </h1>

              {/*<TrustStrip className="pricing-trust-strip" />*/}

              <p className="hero-subtitle v08-subheadline">
                BotSpot translates your intuition into precision-grade automation. Experience a smarter, faster, more profitable way to invest. <strong>Try it free.</strong>
              </p>

              <div className="v08-form-container-override">
                {/* Bonus Offer Container - Reuses inline-checkout-container width + terms-checkbox-container layout */}
                <div className={`inline-checkout-container terms-checkbox-container v08-bonus-offer ${timeRemaining <= 0 ? 'v08-bonus-expired' : ''}`}>
                  <input
                    type="checkbox"
                    checked={bonusOptIn}
                    onChange={(e) => setBonusOptIn(e.target.checked)}
                    className="terms-checkbox"
                    disabled={timeRemaining <= 0}
                  />
                  <span className="terms-text">
                    {timeRemaining > 0 ? (
                      <>
                        <strong className="v08-bonus-headline">YES! Show me the FREE AI Trading Prompt Library</strong>
                        7 battle-tested strategy prompts you can build in seconds.
                        <span className="v08-bonus-timer">
                          Offer expires in <span className="v08-timer-value">{formatTime(timeRemaining)}</span>
                        </span>
                      </>
                    ) : (
                      <span className="v08-expired-text">Bonus offer has expired</span>
                    )}
                  </span>
                </div>

                <InlineCheckoutForm
                  onContactSuccess={handleContactSuccess}
                  contactButtonLabel="START MY FREE TRIAL →"
                />
                <p className="v08-no-cc-text">No credit card required. Cancel anytime.</p>
              </div>

              {showLoginHint && (
                <div className="v08-login-hint">
                  Already a member? <button type="button" onClick={handleLoginClick} className="v08-login-btn">Log in</button>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </>
  );
}
